import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { playerProfileService } from '../../../services/playerMasterService';
import { playerChangeNotificationService } from '../../../services/playerChangeNotificationService';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';

const BulkEditModal = ({ selectedPlayers, onClose, onPlayersUpdated }) => {
  const [activeFields, setActiveFields] = useState({});
  const [bulkData, setBulkData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const editableFields = [
    { field: 'currentClub', label: 'Current Club', type: 'text' },
    { field: 'currentLeague', label: 'Current League', type: 'text' },
    { field: 'currentCountry', label: 'Current Country', type: 'text' },
    { field: 'contractType', label: 'Contract Type', type: 'select', options: [
      { value: 'permanent', label: 'Permanent' },
      { value: 'loan', label: 'Loan' },
      { value: 'free_agent', label: 'Free Agent' }
    ]},
    { field: 'availableForTransfer', label: 'Available for Transfer', type: 'select', options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' }
    ]},
    { field: 'availableForLoan', label: 'Available for Loan', type: 'select', options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' }
    ]},
    { field: 'profileVisibility', label: 'Profile Visibility', type: 'select', options: [
      { value: 'public', label: 'Public' },
      { value: 'private', label: 'Private' },
      { value: 'restricted', label: 'Restricted' }
    ]},
    { field: 'verifiedStatus', label: 'Verified Status', type: 'select', options: [
      { value: 'true', label: 'Verified' },
      { value: 'false', label: 'Not Verified' }
    ]},
    { field: 'agentName', label: 'Agent Name', type: 'text' },
    { field: 'agencyName', label: 'Agency Name', type: 'text' }
  ];

  const toggleField = (field) => {
    setActiveFields(prev => ({
      ...prev,
      [field]: !prev?.[field]
    }));
    if (activeFields?.[field]) {
      // Remove field from bulk data if unchecked
      const newData = { ...bulkData };
      delete newData?.[field];
      setBulkData(newData);
      
      // Remove validation error
      const newErrors = { ...validationErrors };
      delete newErrors?.[field];
      setValidationErrors(newErrors);
    }
  };

  const handleFieldChange = (field, value) => {
    setBulkData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors?.[field]) {
      const newErrors = { ...validationErrors };
      delete newErrors?.[field];
      setValidationErrors(newErrors);
    }
  };

  const validateBulkData = () => {
    const errors = {};

    Object.keys(activeFields)?.forEach(field => {
      if (activeFields?.[field] && !bulkData?.[field]) {
        errors[field] = 'This field is required when selected';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSaveClick = () => {
    if (Object.keys(activeFields)?.filter(key => activeFields?.[key])?.length === 0) {
      alert('Please select at least one field to update');
      return;
    }

    if (!validateBulkData()) {
      alert('Please fill in all selected fields');
      return;
    }

    setShowConfirmDialog(true);
  };

  const saveBulkChanges = async () => {
    setSaving(true);
    try {
      const updateData = {};
      
      // Only include active fields
      Object.keys(activeFields)?.forEach(field => {
        if (activeFields?.[field] && bulkData?.[field] !== undefined) {
          let value = bulkData?.[field];
          
          // Convert string booleans to actual booleans
          if (value === 'true') value = true;
          if (value === 'false') value = false;
          
          updateData[field] = value;
        }
      });

      const fieldLabels = {
        currentClub: 'Current Club',
        currentLeague: 'Current League',
        currentCountry: 'Current Country',
        contractType: 'Contract Type',
        availableForTransfer: 'Available for Transfer',
        availableForLoan: 'Available for Loan',
        profileVisibility: 'Profile Visibility',
        verifiedStatus: 'Verified Status',
        agentName: 'Agent Name',
        agencyName: 'Agency Name'
      };

      const changedFields = Object.keys(updateData)?.map(field => ({
        field,
        label: fieldLabels?.[field] || field,
        oldValue: 'Various',
        newValue: updateData?.[field]
      }));

      // Update each player
      const updatePromises = selectedPlayers?.map(async (player) => {
        try {
          const result = await playerProfileService?.update(player?.id, updateData);
          if (result?.error) {
            console.error(`Failed to update player ${player?.fullName}:`, result?.error);
            return { success: false, player, error: result?.error };
          }
          return { success: true, player, data: result?.data };
        } catch (error) {
          console.error(`Error updating player ${player?.fullName}:`, error);
          return { success: false, player, error };
        }
      });

      const results = await Promise.all(updatePromises);
      const successCount = results?.filter(r => r?.success)?.length;
      const failCount = results?.filter(r => !r?.success)?.length;

      // Send bulk notification
      if (successCount > 0) {
        await playerChangeNotificationService?.sendChangeNotification(
          { fullName: `${successCount} Players`, id: 'bulk' },
          changedFields,
          'Bulk Edit'
        );
      }

      setShowConfirmDialog(false);
      onPlayersUpdated?.();

      if (failCount === 0) {
        alert(`Successfully updated ${successCount} player(s)!`);
        onClose();
      } else {
        alert(`Updated ${successCount} player(s). Failed to update ${failCount} player(s). Check console for details.`);
      }
    } catch (error) {
      console.error('Error in bulk update:', error);
      alert(`Failed to update players: ${error?.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Bulk Edit Players</h2>
              <p className="text-sm text-gray-600 mt-1">
                Editing {selectedPlayers?.length} player(s)
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 font-medium">Bulk Edit Instructions</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Select the fields you want to update, enter the new values, and click Save. 
                    The selected values will be applied to all {selectedPlayers?.length} selected players.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {editableFields?.map(({ field, label, type, options }) => (
                <div key={field} className="border rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={activeFields?.[field] || false}
                      onChange={() => toggleField(field)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-900 block mb-2">
                        {label}
                      </label>
                      {type === 'select' ? (
                        <Select
                          value={bulkData?.[field] || ''}
                          onChange={(e) => handleFieldChange(field, e?.target?.value)}
                          disabled={!activeFields?.[field]}
                          className={validationErrors?.[field] ? 'border-red-500' : ''}
                        >
                          <option value="">Select {label}</option>
                          {options?.map(opt => (
                            <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          type={type}
                          value={bulkData?.[field] || ''}
                          onChange={(e) => handleFieldChange(field, e?.target?.value)}
                          disabled={!activeFields?.[field]}
                          placeholder={`Enter ${label?.toLowerCase()}`}
                          className={validationErrors?.[field] ? 'border-red-500' : ''}
                        />
                      )}
                      {validationErrors?.[field] && (
                        <p className="text-xs text-red-500 mt-1">{validationErrors?.[field]}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t bg-gray-50">
            <p className="text-sm text-gray-600">
              {Object.keys(activeFields)?.filter(key => activeFields?.[key])?.length} field(s) selected
            </p>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveClick}
                loading={saving}
                disabled={saving || Object.keys(activeFields)?.filter(key => activeFields?.[key])?.length === 0}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Icon name="Save" size={16} className="mr-2" />
                Save Bulk Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={saveBulkChanges}
        title="Confirm Bulk Update"
        message={`Are you sure you want to update ${selectedPlayers?.length} player(s)? This action will apply the selected changes to all selected players and notify relevant stakeholders.`}
        confirmText="Update All Players"
        cancelText="Cancel"
        variant="warning"
        loading={saving}
      />
    </>
  );
};

export default BulkEditModal;